import React, { useState, useEffect } from 'react';
import { Download, Smartphone, Share } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const InstallPage: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    // Check if already installed
    const installed = window.matchMedia('(display-mode: standalone)').matches;
    setIsInstalled(installed);

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(promptEvent);
      (window as any).deferredInstallPrompt = promptEvent;
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Try to get existing deferred prompt
    if ((window as any).deferredInstallPrompt) {
      setDeferredPrompt((window as any).deferredInstallPrompt);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    const prompt = deferredPrompt || (window as any).deferredInstallPrompt;

    if (!prompt) {
      console.log('No install prompt available');
      return;
    }

    try {
      await prompt.prompt();
      const { outcome } = await prompt.userChoice;

      if (outcome === 'accepted') {
        setShowSuccess(true);
        setTimeout(() => {
          setIsInstalled(true);
        }, 2000);
      }
    } catch (error) {
      console.error('Error during install:', error);
    }

    setDeferredPrompt(null);
    (window as any).deferredInstallPrompt = null;
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '24px',
      padding: '48px 32px',
      maxWidth: '500px',
      width: '100%',
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      textAlign: 'center' as const
    },
    logo: {
      width: '120px',
      height: '120px',
      margin: '0 auto 24px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderRadius: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)'
    },
    title: {
      fontSize: '32px',
      fontWeight: '700',
      color: '#1e293b',
      margin: '0 0 12px 0'
    },
    subtitle: {
      fontSize: '18px',
      color: '#64748b',
      margin: '0 0 32px 0',
      lineHeight: '1.6'
    },
    button: {
      width: '100%',
      padding: '20px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '16px',
      fontSize: '20px',
      fontWeight: '700',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 16px rgba(102, 126, 234, 0.4)',
      marginBottom: '24px'
    },
    instructionBox: {
      backgroundColor: '#f8fafc',
      padding: '24px',
      borderRadius: '16px',
      marginTop: '24px',
      textAlign: 'left' as const
    },
    instructionTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1e293b',
      margin: '0 0 16px 0',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    step: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      marginBottom: '16px',
      fontSize: '15px',
      color: '#475569',
      lineHeight: '1.6'
    },
    stepNumber: {
      minWidth: '28px',
      height: '28px',
      backgroundColor: '#667eea',
      color: 'white',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: '700',
      fontSize: '14px'
    },
    icon: {
      fontSize: '48px',
      marginBottom: '8px'
    },
    feature: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px',
      backgroundColor: '#f8fafc',
      borderRadius: '12px',
      marginBottom: '12px',
      fontSize: '15px',
      color: '#475569'
    },
    checkmark: {
      minWidth: '24px',
      height: '24px',
      backgroundColor: '#22c55e',
      color: 'white',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: '700'
    },
    successMessage: {
      backgroundColor: '#22c55e',
      color: 'white',
      padding: '16px',
      borderRadius: '12px',
      marginTop: '16px',
      fontSize: '16px',
      fontWeight: '600'
    }
  };

  if (isInstalled) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logo}>
            <Smartphone size={64} color="white" />
          </div>
          <h1 style={styles.title}>Already Installed!</h1>
          <p style={styles.subtitle}>
            Bradanna Field App is already installed on your device.
          </p>
          <div style={styles.icon}>✅</div>
          <p style={{ fontSize: '16px', color: '#64748b', marginTop: '16px' }}>
            You can find it on your home screen or app drawer.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <Download size={64} color="white" />
        </div>

        <h1 style={styles.title}>Install Bradanna Field App</h1>
        <p style={styles.subtitle}>
          Get quick access and work offline with our mobile app
        </p>

        {/* Features */}
        <div style={{ marginBottom: '32px' }}>
          <div style={styles.feature}>
            <div style={styles.checkmark}>✓</div>
            <span>Works offline - no internet required</span>
          </div>
          <div style={styles.feature}>
            <div style={styles.checkmark}>✓</div>
            <span>Instant access from home screen</span>
          </div>
          <div style={styles.feature}>
            <div style={styles.checkmark}>✓</div>
            <span>Auto-sync when back online</span>
          </div>
        </div>

        {/* Android/Chrome - Show install button */}
        {!isIOS && deferredPrompt && (
          <>
            <button
              style={styles.button}
              onClick={handleInstall}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.6)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(102, 126, 234, 0.4)';
              }}
            >
              <Download size={24} />
              Install App Now
            </button>
            {showSuccess && (
              <div style={styles.successMessage}>
                App installed successfully! 🎉
              </div>
            )}
          </>
        )}

        {/* iOS Instructions */}
        {isIOS && (
          <div style={styles.instructionBox}>
            <div style={styles.instructionTitle}>
              <Share size={20} color="#667eea" />
              How to Install on iPhone/iPad
            </div>

            <div style={styles.step}>
              <div style={styles.stepNumber}>1</div>
              <div>
                Tap the <strong>Share</strong> button at the bottom of Safari
                <div style={{ fontSize: '32px', margin: '8px 0' }}>⬆️</div>
              </div>
            </div>

            <div style={styles.step}>
              <div style={styles.stepNumber}>2</div>
              <div>
                Scroll down and tap <strong>"Add to Home Screen"</strong>
                <div style={{ fontSize: '32px', margin: '8px 0' }}>➕</div>
              </div>
            </div>

            <div style={styles.step}>
              <div style={styles.stepNumber}>3</div>
              <div>
                Tap <strong>"Add"</strong> in the top right corner
              </div>
            </div>

            <div style={styles.step}>
              <div style={styles.stepNumber}>4</div>
              <div>
                The app icon will appear on your home screen!
              </div>
            </div>
          </div>
        )}

        {/* Android/Chrome - No prompt available */}
        {!isIOS && !deferredPrompt && (
          <div style={styles.instructionBox}>
            <div style={styles.instructionTitle}>
              <Download size={20} color="#667eea" />
              How to Install on Android/Chrome
            </div>

            <div style={styles.step}>
              <div style={styles.stepNumber}>1</div>
              <div>
                Tap the <strong>menu button</strong> (three dots) in the top right of Chrome
              </div>
            </div>

            <div style={styles.step}>
              <div style={styles.stepNumber}>2</div>
              <div>
                Select <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong>
              </div>
            </div>

            <div style={styles.step}>
              <div style={styles.stepNumber}>3</div>
              <div>
                Tap <strong>"Install"</strong> to confirm
              </div>
            </div>

            <div style={styles.step}>
              <div style={styles.stepNumber}>4</div>
              <div>
                The app will be added to your home screen!
              </div>
            </div>
          </div>
        )}

        <p style={{
          fontSize: '14px',
          color: '#94a3b8',
          marginTop: '32px',
          borderTop: '1px solid #e2e8f0',
          paddingTop: '24px'
        }}>
          Need help? Contact your administrator
        </p>
      </div>
    </div>
  );
};
